import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../socket";
import { usePageFocus } from "../pagefocus";
import { GameSocketMessageEventTypes, GameSocketNotifyMessageTypes, IGameSocketNotifyMessage } from "../../../src/models/game/socket";
import { Intent, OverlayToaster } from "@blueprintjs/core";
import NotificationComponent from "./notificationcomponent";
import styles from './notifications.module.css';
import dynamic from "next/dynamic";

const OverlayToasterClient = dynamic(
    () => import("../overlaytoasterclient"),
    { ssr: false },
);

type NotificationContextProps = {
    // used to add additional styling to the toasts
    // for example, we can offset toasts from the bottom
    // when the game play view is up
    className?: string;
    setClassName: Dispatch<SetStateAction<string | undefined>>
}

const NotificationContext = createContext<NotificationContextProps>(null);

type NotificationsProps = {
    children?: React.ReactNode;
}

export function Notifications(
    {
        children,
    }: NotificationsProps,
): React.ReactNode {
    const toasterRef = useRef<OverlayToaster | null>(null);
    const { socket } = useSocket();
    const isPageFocused = usePageFocus();
    const notifications = useRef<IGameSocketNotifyMessage[]>([]);
    const [className, setClassName] = useState<string | undefined>(undefined);
    const [isToasterReady, setIsToasterReady] = useState(false);

    const flushNotifications = useCallback(
        () => {
            const toaster = toasterRef.current;
            if (!isPageFocused || !isToasterReady || !toaster) {
                return;
            }

            const pendingNotifications = notifications.current;
            notifications.current = [];

            for (const message of pendingNotifications) {
                const key = `notif_${message.gameId}_${Math.random()}`;

                toaster.show(
                    {
                        message: <NotificationComponent
                            message={message}
                            onClose={() => toasterRef.current?.dismiss(key)}
                        />,
                        className: styles.notification,
                        intent: Intent.PRIMARY,
                    },
                    key,
                );
            }
        },
        [isPageFocused, isToasterReady],
    );

    useEffect(() => {
        flushNotifications();
    }, [flushNotifications]);

    const createToast = useCallback(
        (message: IGameSocketNotifyMessage): void => {
            notifications.current.push({
                ...message,
                ts: Date.now(),
            });
            flushNotifications();
        },
        [flushNotifications],
    );

    // leaving this in for easy testing
    /*
    useEffect(
        () => {
            const data: IGameSocketNotifyMessage = {
                "type": GameSocketNotifyMessageTypes.Notification,
                "gameId": 1498,
                "data": {
                    "key": "GameNotificationInvite",
                    "titleKey": "GameNotificationInviteTitle",
                    "blocks": {
                        "game": {
                            "type": GameNotificationBlockType.Game,
                            "game": {
                                "id": 1498,
                                "name": "other track",
                                "map": {
                                    "id": 1,
                                    "name": "World"
                                }
                            }
                        },
                        "inviter": {
                            "type": GameNotificationBlockType.User,
                            "user": {
                                "id": 4,
                                "name": "porty",
                                "avatar": "110_HeadFront.png"
                            }
                        }
                    }
                }
            };

            const onKey = () => {
                createToast(data);
            };

            window.onkeydown = (e) => {
                if (e.key === 'n') {
                    onKey();
                }
            };

            return () => {
                window.onkeydown = null;
            };
        },
        [createToast],
    );
    */

    useEffect(
        () => {
            if (!socket) {
                return;
            }

            const onActiveGameChange = (message: IGameSocketNotifyMessage) => {
                switch (message.type) {
                    case GameSocketNotifyMessageTypes.Notification:
                        createToast(message);

                        break;
                }
            };

            socket.on(GameSocketMessageEventTypes.Notify, onActiveGameChange);

            return () => {
                socket.off(GameSocketMessageEventTypes.Notify, onActiveGameChange);
            };
        },
        [createToast, socket],
    );

    const value = useMemo(
        (): NotificationContextProps => ({
            className,
            setClassName,
        }),
        [className, setClassName],
    );

    const onReady = useCallback(
        (toaster: OverlayToaster | null) => {
            toasterRef.current = toaster;
            setIsToasterReady(Boolean(toaster));
        },
        [],
    );

    return (
        <NotificationContext.Provider value={value}>
            <OverlayToasterClient
                className={className}
                onReady={onReady}
            />
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationContext);
}
